import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Diamètre du cercle en pixels. Défaut 26 (taille du brand book). */
  size?: number;
  /** Initiale(s) à centrer dans le cercle. Ignoré si `children` est fourni. */
  initials?: string;
  /** Photo de l'utilisateur. Prime sur les initiales, qui restent le repli. */
  src?: string;
  /** Contenu personnalisé (icône, image…) à la place des initiales. */
  children?: ReactNode;
}

/**
 * Avatar : cercle plein gris encre avec l'initiale centrée en blanc, ou la
 * photo de l'utilisateur si `src` est fourni (recadrée en `cover`).
 * Brand book : 26px · border-radius:50% · bg ink-500 · color #FFFFFF · Inter 600.
 * La taille de police suit le diamètre (~0.42x) pour rester lisible à toutes les tailles.
 */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { className, size = 26, initials, src, children, style, ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-ink-500 font-semibold leading-none text-white',
        className,
      )}
      // size en px de reference (1920) : rendu en rem pour suivre l'echelle globale.
      style={{
        width: `${size / 16}rem`,
        height: `${size / 16}rem`,
        fontSize: `${Math.round(size * 0.42) / 16}rem`,
        ...style,
      }}
      {...props}
    >
      {children ??
        (src ? (
          <img src={src} alt="" className="h-full w-full object-cover" />
        ) : (
          initials
        ))}
    </span>
  );
});
